import React, { useCallback, useEffect, useRef, useState } from "react";
import { Controller, useFieldArray, useForm, useWatch } from "react-hook-form";
import { Navigate } from "react-router-dom";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import PublishIcon from "@mui/icons-material/Publish";
import RefreshIcon from "@mui/icons-material/Refresh";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Divider,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  MenuItem,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useDispatch } from "react-redux";
import PageHeaderLayout from "../../components/Layout/PageHeaderLayout";
import { usePermission } from "../../hooks/usePermission";
import { QuickBookService } from "./Service";
import {
  CATALOG_MODES,
  CATALOG_SOURCE_TYPES,
  MAX_CATALOG_DEPTH,
  catalogBreadcrumbText,
  emptyCatalogNode,
  normalizeCatalogRoots,
  isSafeImageReference,
  optionId,
  optionImage,
  optionLabel,
  toCatalogDraftPayload,
  validateCatalogTree,
} from "./catalogModel";
import { quickBookModeLabel } from "./display";

const ok = (response) => response?.success === 1 || response?.success === "1";
const hasUnsafeText = (value) => /[<>]/.test(value) || Array.from(value).some((character) => {
  const code = character.charCodeAt(0);
  return (code < 32 && ![9, 10, 13].includes(code)) || code === 127;
});
const plainText = (max, required = false) => (value) => {
  const content = String(value || "").trim();
  if (required && !content) return "Required";
  if (content.length > max) return `Maximum ${max} characters`;
  return !hasUnsafeText(content) || "Plain text only";
};
const urlRule = (value) => {
  const content = String(value || "").trim();
  if (!content) return true;
  return isSafeImageReference(content) || "Use a safe HTTP(S) URL or media path (no traversal or backslashes)";
};
const safeImageUrl = (value) => {
  try {
    const parsed = new URL(String(value || ""));
    return ["http:", "https:"].includes(parsed.protocol) && !parsed.username && !parsed.password ? value : "";
  } catch {
    return "";
  }
};

const sourceTypeLabel = (value) => ({
  CATEGORY: "Category",
  SUBCATEGORY: "Subcategory",
  MASTER_SERVICE: "Master service",
  CHILD_SERVICE: "Child service",
  BRAND: "Brand",
}[value] || value);

const offeringText = (offering) => {
  const brand = offering.serviceBrand?.title || offering.serviceBrand?.label || offering.brandTitle;
  const breadcrumb = catalogBreadcrumbText(offering.catalogBreadcrumb);
  return [offering.customerTitle || offering.serviceId, brand, breadcrumb].filter(Boolean).join(" · ");
};
const money = (paise) => new Intl.NumberFormat("en-IN", {
  style: "currency",
  currency: "INR",
  maximumFractionDigits: 2,
}).format(Number(paise || 0) / 100);
const CANONICAL_OPTION_LIMIT = 50;

function CanonicalSourceField({ control, mode, path, parentSource, disabled, setValue }) {
  const sourceType = useWatch({ control, name: `${path}.sourceType` });
  const sourceId = useWatch({ control, name: `${path}.sourceId` });
  const canonicalLabel = useWatch({ control, name: `${path}.canonicalLabel` });
  const canonicalImageUrl = useWatch({ control, name: `${path}.canonicalImageUrl` });
  const [options, setOptions] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [searchAll, setSearchAll] = useState(false);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const hasParent = Boolean(parentSource);
  const parentSourceType = parentSource?.type;
  const parentSourceId = parentSource?.id;
  const parentKey = hasParent ? `${parentSourceType || ""}:${parentSourceId || ""}` : "ROOT";
  const previousParentKey = useRef(parentKey);

  useEffect(() => {
    if (previousParentKey.current !== parentKey) {
      if (!searchAll) {
        setValue(`${path}.sourceId`, null, { shouldDirty: true });
        setValue(`${path}.canonicalLabel`, "", { shouldDirty: true });
        setValue(`${path}.canonicalImageUrl`, "", { shouldDirty: true });
      }
      previousParentKey.current = parentKey;
    }
  }, [parentKey, path, searchAll, setValue]);

  useEffect(() => {
    setOptions([]);
    setSearch("");
    setPage(1);
    setHasMore(false);
  }, [mode, parentKey, searchAll, sourceType]);

  useEffect(() => {
    if (!open) {
      setLoading(false);
      return undefined;
    }
    if (hasParent && !searchAll && !parentSourceId) {
      setOptions([]);
      setError("");
      setLoading(false);
      return undefined;
    }
    let active = true;
    const timer = setTimeout(() => {
      setLoading(true);
      setError("");
      QuickBookService.canonicalOptions({
        mode,
        sourceType,
        searchAll,
        search: search.trim(),
        page,
        limit: CANONICAL_OPTION_LIMIT,
        selectedId: sourceId || undefined,
        ...(hasParent && !searchAll ? { parentSourceType, parentSourceId } : {}),
      }).then((response) => {
        if (!active) return;
        if (ok(response)) {
          const data = response.data;
          const nextOptions = Array.isArray(data) ? data : data?.items || data?.options || [];
          setOptions((current) => page === 1
            ? nextOptions
            : [...current, ...nextOptions.filter((option) => !current.some((item) => optionId(item) === optionId(option)))]);
          const selected = nextOptions.find((option) => optionId(option) === Number(sourceId));
          if (selected) {
            setValue(`${path}.canonicalLabel`, optionLabel(selected), { shouldDirty: false });
            setValue(`${path}.canonicalImageUrl`, optionImage(selected), { shouldDirty: false });
          } else if (sourceId && page === 1) {
            if (hasParent && !searchAll) {
              setSearchAll(true);
              setLoading(false);
              return;
            }
            setValue(`${path}.sourceId`, null, { shouldDirty: true });
            setValue(`${path}.canonicalLabel`, "", { shouldDirty: true });
            setValue(`${path}.canonicalImageUrl`, "", { shouldDirty: true });
            setError("The saved source is no longer available. Select it again.");
          }
          setHasMore(Boolean(data?.pagination?.hasMore));
        } else {
          if (page === 1) setOptions([]);
          setError(response?.message || "Canonical options unavailable");
        }
        setLoading(false);
      }).catch(() => {
        if (!active) return;
        if (page === 1) setOptions([]);
        setError("Canonical options unavailable");
        setLoading(false);
      });
    }, 300);
    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [hasParent, mode, open, page, parentSourceId, parentSourceType, path, search, searchAll, setValue, sourceId, sourceType]);

  const fallback = sourceId ? {
    id: sourceId,
    label: canonicalLabel || `${sourceTypeLabel(sourceType)} #${sourceId}`,
    imageUrl: canonicalImageUrl,
  } : null;
  const choices = fallback && !options.some((option) => optionId(option) === Number(sourceId))
    ? [fallback, ...options]
    : options;

  return (
    <Stack spacing={0.25}>
      <Controller
        name={`${path}.sourceId`}
        control={control}
        rules={{ required: "Select a canonical source", validate: (value) => Number(value) > 0 || "Select a canonical source" }}
        render={({ field, fieldState }) => (
          <Autocomplete
            options={choices}
            value={choices.find((option) => optionId(option) === Number(field.value)) || null}
            open={open}
            onOpen={() => setOpen(true)}
            onClose={() => setOpen(false)}
            loading={loading}
            disabled={disabled || !sourceType}
            inputValue={search}
            filterOptions={(items) => items}
            onInputChange={(_, value, reason) => {
              if (["input", "clear", "reset"].includes(reason)) {
                setSearch(value);
                setPage(1);
              }
            }}
            ListboxProps={{
              onScroll: (event) => {
                const list = event.currentTarget;
                if (hasMore && !loading && list.scrollTop + list.clientHeight >= list.scrollHeight - 16) {
                  setPage((current) => current + 1);
                }
              },
            }}
            getOptionLabel={(option) => option.hierarchyName || optionLabel(option)}
            isOptionEqualToValue={(option, value) => optionId(option) === optionId(value)}
            onChange={(_, value) => {
              setError("");
              field.onChange(value ? optionId(value) : null);
              setValue(`${path}.canonicalLabel`, value ? optionLabel(value) : "", { shouldDirty: true });
              setValue(`${path}.canonicalImageUrl`, value ? optionImage(value) : "", { shouldDirty: true });
            }}
            noOptionsText={error || "No canonical options"}
            renderInput={(params) => (
              <TextField
                {...params}
                size="small"
                required
                label={`Canonical ${sourceTypeLabel(sourceType)}`}
                error={Boolean(fieldState.error) || Boolean(error)}
                helperText={fieldState.error?.message || error || "Required canonical link"}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: <>{loading ? <CircularProgress color="inherit" size={16} /> : null}{params.InputProps.endAdornment}</>,
                }}
              />
            )}
          />
        )}
      />
      {hasParent && (
        <FormControlLabel
          sx={{ m: 0, alignSelf: "flex-start" }}
          control={<Switch size="small" checked={searchAll} onChange={(event) => setSearchAll(event.target.checked)} disabled={disabled} />}
          label={<Typography sx={{ fontSize: ".75rem" }}>Search all</Typography>}
        />
      )}
    </Stack>
  );
}

function CatalogNode({ control, setValue, path, index, depth, parentSource, offerings, disabled, move, remove, count }) {
  const kind = useWatch({ control, name: `${path}.kind` });
  const sourceType = useWatch({ control, name: `${path}.sourceType` });
  const sourceId = useWatch({ control, name: `${path}.sourceId` });
  const label = useWatch({ control, name: `${path}.label` });
  const canonicalLabel = useWatch({ control, name: `${path}.canonicalLabel` });
  const mode = useWatch({ control, name: "mode" });

  return (
    <Card variant="outlined" sx={{ p: { xs: 1, md: 1.5 }, borderLeft: `${Math.min(depth + 1, 6)}px solid`, borderLeftColor: kind === "OFFERING" ? "success.light" : "primary.light" }}>
      <Stack spacing={1.25}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Chip size="small" color={kind === "OFFERING" ? "success" : "primary"} label={`Level ${depth} · ${kind === "OFFERING" ? "Offering" : "Navigation"}`} />
          <Typography sx={{ flex: 1, fontSize: ".75rem", fontWeight: 600 }} noWrap>{label || canonicalLabel || "New node"}</Typography>
          <Tooltip title="Move up"><span><IconButton size="small" aria-label={`Move node ${index + 1} up`} disabled={disabled || index === 0} onClick={() => move(index, index - 1)}><ArrowUpwardIcon fontSize="small" /></IconButton></span></Tooltip>
          <Tooltip title="Move down"><span><IconButton size="small" aria-label={`Move node ${index + 1} down`} disabled={disabled || index === count - 1} onClick={() => move(index, index + 1)}><ArrowDownwardIcon fontSize="small" /></IconButton></span></Tooltip>
          <Tooltip title="Remove"><span><IconButton size="small" color="error" aria-label={`Remove node ${index + 1}`} disabled={disabled} onClick={() => remove(index)}><DeleteOutlineIcon fontSize="small" /></IconButton></span></Tooltip>
        </Box>

        <Grid container spacing={1}>
          <Grid item xs={12} sm={6} md={3}>
            <Controller name={`${path}.kind`} control={control} render={({ field }) => (
              <TextField
                {...field}
                select
                fullWidth
                size="small"
                label="Node kind"
                disabled={disabled}
                onChange={(event) => {
                  field.onChange(event);
                  if (event.target.value === "OFFERING") setValue(`${path}.children`, [], { shouldDirty: true });
                  else setValue(`${path}.offeringId`, "", { shouldDirty: true });
                }}
              >
                {depth < MAX_CATALOG_DEPTH && <MenuItem value="NAVIGATION">Navigation</MenuItem>}
                <MenuItem value="OFFERING">Offering</MenuItem>
              </TextField>
            )} />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Controller name={`${path}.sourceType`} control={control} rules={{ required: "Required" }} render={({ field, fieldState }) => (
              <TextField
                {...field}
                select
                fullWidth
                size="small"
                label="Canonical type"
                required
                disabled={disabled}
                error={Boolean(fieldState.error)}
                helperText={fieldState.error?.message}
                onChange={(event) => {
                  field.onChange(event);
                  setValue(`${path}.sourceId`, null, { shouldDirty: true });
                  setValue(`${path}.canonicalLabel`, "", { shouldDirty: true });
                  setValue(`${path}.canonicalImageUrl`, "", { shouldDirty: true });
                }}
              >
                {CATALOG_SOURCE_TYPES.map((value) => <MenuItem key={value} value={value}>{sourceTypeLabel(value)}</MenuItem>)}
              </TextField>
            )} />
          </Grid>
          <Grid item xs={12} md={6}>
            <CanonicalSourceField control={control} mode={mode} path={path} parentSource={parentSource} disabled={disabled} setValue={setValue} />
          </Grid>
          <Grid item xs={12} md={6}>
            <Controller name={`${path}.label`} control={control} rules={{ validate: plainText(160, true) }} render={({ field, fieldState }) => <TextField {...field} fullWidth size="small" label="Customer label (English)" required disabled={disabled} error={Boolean(fieldState.error)} helperText={fieldState.error?.message || "Shown instead of the canonical label"} inputProps={{ maxLength: 160 }} />} />
          </Grid>
          <Grid item xs={12} md={6}>
            <Controller name={`${path}.subtext`} control={control} rules={{ validate: plainText(500) }} render={({ field, fieldState }) => <TextField {...field} fullWidth size="small" label="Customer subtext (English, optional)" disabled={disabled} error={Boolean(fieldState.error)} helperText={fieldState.error?.message} inputProps={{ maxLength: 500 }} />} />
          </Grid>
          <Grid item xs={12} md={kind === "OFFERING" ? 6 : 12}>
            <Controller name={`${path}.imageOverrideUrl`} control={control} rules={{ validate: urlRule }} render={({ field, fieldState }) => <TextField {...field} fullWidth size="small" label="Image override URL or media path (optional)" disabled={disabled} error={Boolean(fieldState.error)} helperText={fieldState.error?.message || "Blank uses the canonical image; relative paths may show a preview placeholder"} inputProps={{ maxLength: 1000 }} />} />
          </Grid>
          {kind === "OFFERING" && (
            <Grid item xs={12} md={6}>
              <Controller name={`${path}.offeringId`} control={control} rules={{ required: "Select an offering" }} render={({ field, fieldState }) => (
                <Autocomplete
                  options={offerings}
                  value={offerings.find((offering) => offering.serviceId === field.value) || null}
                  getOptionLabel={offeringText}
                  isOptionEqualToValue={(option, value) => option.serviceId === value.serviceId}
                  onChange={(_, value) => field.onChange(value?.serviceId || "")}
                  disabled={disabled}
                  noOptionsText="No offerings are available for this mode"
                  renderInput={(params) => <TextField {...params} size="small" label="Quick Book offering" required error={Boolean(fieldState.error)} helperText={fieldState.error?.message || "Links the customer leaf to existing offering CRUD"} />}
                />
              )} />
            </Grid>
          )}
        </Grid>

        {kind === "NAVIGATION" && depth < MAX_CATALOG_DEPTH && (
          <Box sx={{ pl: { xs: 1, md: 2 }, borderLeft: "1px dashed", borderColor: "divider" }}>
            <CatalogNodes
              control={control}
              setValue={setValue}
              name={`${path}.children`}
              depth={depth + 1}
              parentSource={{ type: sourceType, id: sourceId }}
              offerings={offerings}
              disabled={disabled}
            />
          </Box>
        )}
      </Stack>
    </Card>
  );
}

function CatalogNodes({ control, setValue, name, depth, parentSource, offerings, disabled }) {
  const { fields, append, remove, move } = useFieldArray({ control, name });
  return (
    <Stack spacing={1}>
      {fields.map((field, index) => (
        <CatalogNode
          key={field.id}
          control={control}
          setValue={setValue}
          path={`${name}.${index}`}
          index={index}
          depth={depth}
          parentSource={parentSource}
          offerings={offerings}
          disabled={disabled}
          move={move}
          remove={remove}
          count={fields.length}
        />
      ))}
      <Button
        type="button"
        size="small"
        variant={fields.length ? "text" : "outlined"}
        startIcon={<AddCircleOutlineIcon />}
        disabled={disabled}
        onClick={() => append(emptyCatalogNode(depth, depth === MAX_CATALOG_DEPTH ? "OFFERING" : "NAVIGATION"))}
        sx={{ alignSelf: "flex-start" }}
      >
        Add {depth === 1 ? "root node" : `level ${depth} node`}
      </Button>
    </Stack>
  );
}

function PreviewNodes({ nodes, offerings, mode, depth = 1, ancestors = [] }) {
  return (nodes || []).map((node, index) => {
    const image = safeImageUrl(node.imageOverrideUrl) || safeImageUrl(node.canonicalImageUrl);
    const offering = node.kind === "OFFERING" ? offerings.find((item) => item.serviceId === node.offeringId) : null;
    const breadcrumb = node.kind === "OFFERING"
      ? [...ancestors, node.label || node.canonicalLabel].filter(Boolean).join(" › ")
      : "";
    const price = offering ? (mode === "INSPECTION_FIRST"
      ? `Inspection fee ${money(offering.unitAmountPaise)}`
      : `${money(offering.unitAmountPaise)} / ${offering.unitLabel || "unit"}`) : "";
    return (
      <Box key={node.nodeId || `${depth}-${index}-${node.label}`} sx={{ mb: 1 }}>
        <Card variant="outlined" sx={{ p: 1, bgcolor: node.kind === "OFFERING" ? "success.50" : "background.paper" }}>
          <Stack direction="row" spacing={1} alignItems="center">
            {image ? <Box component="img" src={image} alt="" sx={{ width: 42, height: 42, borderRadius: 1.5, objectFit: "cover", bgcolor: "grey.100" }} /> : <Box sx={{ width: 42, height: 42, borderRadius: 1.5, bgcolor: "grey.200" }} />}
            <Box sx={{ minWidth: 0 }}>
              <Stack direction="row" spacing={0.5} alignItems="center"><Typography color="text.secondary" sx={{ fontSize: ".55rem" }}>L{depth}</Typography><Typography sx={{ fontSize: ".78rem", fontWeight: 700 }} noWrap>{node.label || node.canonicalLabel || "Untitled"}</Typography></Stack>
              {node.subtext && <Typography color="text.secondary" sx={{ fontSize: ".65rem" }} noWrap>{node.subtext}</Typography>}
              {price && <Typography color="success.dark" sx={{ fontSize: ".68rem", fontWeight: 700 }}>{price}</Typography>}
              {breadcrumb && <Typography color="primary.main" sx={{ fontSize: ".58rem" }} noWrap>{breadcrumb}</Typography>}
            </Box>
          </Stack>
        </Card>
        {node.kind === "NAVIGATION" && node.children?.length > 0 && <Box sx={{ ml: 1.5, mt: 1 }}><PreviewNodes nodes={node.children} offerings={offerings} mode={mode} depth={depth + 1} ancestors={[...ancestors, node.label || node.canonicalLabel].filter(Boolean)} /></Box>}
      </Box>
    );
  });
}

export default function QuickBookCatalog() {
  const dispatch = useDispatch();
  const permission = usePermission("service");
  const [mode, setMode] = useState(CATALOG_MODES[0]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [message, setMessage] = useState(null);
  const [treeErrors, setTreeErrors] = useState([]);
  const [draft, setDraft] = useState(null);
  const [published, setPublished] = useState(null);
  const [offerings, setOfferings] = useState([]);
  const [offeringsError, setOfferingsError] = useState("");
  const [publishDialogOpen, setPublishDialogOpen] = useState(false);
  const loadSequence = useRef(0);
  const { control, handleSubmit, reset, setValue, watch, formState: { isDirty } } = useForm({
    mode: "onChange",
    defaultValues: { mode, roots: [] },
  });
  const roots = watch("roots");

  useEffect(() => {
    dispatch({ type: "menuSelect", payload: "quick-book-catalog" });
  }, [dispatch]);

  const loadCatalog = useCallback(async () => {
    const sequence = loadSequence.current + 1;
    loadSequence.current = sequence;
    setLoading(true);
    setLoadError("");
    setOfferingsError("");
    setMessage(null);
    setTreeErrors([]);
    try {
      const [catalogResponse, offeringsResponse] = await Promise.all([
        QuickBookService.catalog(mode),
        QuickBookService.catalogOfferings(mode),
      ]);
      if (sequence !== loadSequence.current) return;
      if (!ok(catalogResponse)) {
        setLoadError(catalogResponse?.message || "Catalog could not be loaded.");
        setDraft(null);
        setPublished(null);
        reset({ mode, roots: [] });
      } else {
        const nextDraft = catalogResponse.data?.draft || null;
        setDraft(nextDraft);
        setPublished(catalogResponse.data?.published || null);
        reset({ mode, roots: normalizeCatalogRoots(nextDraft?.roots || []) });
      }
      if (ok(offeringsResponse)) {
        setOfferings((offeringsResponse.data?.items || []).filter((item) => item.status !== "ARCHIVED" && (!item.mode || item.mode === mode)));
      } else {
        setOfferings([]);
        setOfferingsError(offeringsResponse?.message || "Offerings could not be loaded.");
      }
    } catch {
      if (sequence !== loadSequence.current) return;
      setLoadError("Catalog could not be loaded. Please try again.");
      setOfferings([]);
      setOfferingsError("Offerings could not be loaded.");
    } finally {
      if (sequence === loadSequence.current) setLoading(false);
    }
  }, [mode, reset]);

  useEffect(() => { loadCatalog(); }, [loadCatalog]);

  if (!permission.canView) return <Navigate to="/unauthorized" replace />;

  const saveDraft = async (values) => {
    const validation = validateCatalogTree(values.roots, new Set(offerings.map((item) => item.serviceId)));
    setTreeErrors(validation);
    if (validation.length) return;
    setSaving(true);
    setMessage(null);
    let response;
    try {
      response = await QuickBookService.saveCatalogDraft(toCatalogDraftPayload({
        mode,
        expectedVersion: draft?.version ?? 0,
        roots: values.roots,
      }));
    } catch {
      setMessage({ severity: "error", text: "Catalog draft could not be saved. Please try again." });
      setSaving(false);
      return;
    }
    setSaving(false);
    if (ok(response)) {
      await loadCatalog();
      setMessage({ severity: "success", text: response.message || "Catalog draft saved." });
      return;
    }
    const conflict = response?.error?.code === "QUICK_BOOK_VERSION_CONFLICT";
    setMessage({ severity: conflict ? "warning" : "error", reload: conflict, text: conflict ? "This draft changed in another session. Reload it before editing again." : response?.message || "Catalog draft could not be saved." });
  };

  const publish = async () => {
    setPublishDialogOpen(false);
    const validation = validateCatalogTree(roots, new Set(offerings.map((item) => item.serviceId)));
    setTreeErrors(validation);
    if (validation.length) return;
    setPublishing(true);
    setMessage(null);
    let response;
    try {
      response = await QuickBookService.publishCatalog({ mode, expectedVersion: draft?.version ?? 0 });
    } catch {
      setMessage({ severity: "error", text: "Catalog could not be published. Please try again." });
      setPublishing(false);
      return;
    }
    setPublishing(false);
    if (ok(response)) {
      await loadCatalog();
      setMessage({ severity: "success", text: response.message || "Catalog published." });
      return;
    }
    const conflict = response?.error?.code === "QUICK_BOOK_VERSION_CONFLICT";
    setMessage({ severity: conflict ? "warning" : "error", reload: conflict, text: conflict ? "This draft changed in another session. Reload it before publishing." : response?.message || "Catalog could not be published." });
  };

  const busy = loading || saving || publishing;
  const publishedAt = published?.publishedAt ? new Date(published.publishedAt).toLocaleString() : null;
  const changeMode = (nextMode) => {
    if (isDirty) {
      setMessage({ severity: "warning", text: "Save or reload the current draft before switching modes." });
      return;
    }
    setMode(nextMode);
  };
  const requestPublish = () => {
    if (isDirty) {
      setMessage({ severity: "warning", text: "Save the current draft before publishing." });
      return;
    }
    setPublishDialogOpen(true);
  };

  return (
    <>
      <PageHeaderLayout headerTitle="Quick Book Catalog Structure">
        <Tooltip title="Discard local edits and reload"><span><IconButton aria-label="Reload catalog" onClick={loadCatalog} disabled={busy}><RefreshIcon /></IconButton></span></Tooltip>
      </PageHeaderLayout>

      <Card sx={{ mb: 1.5 }}>
        <Tabs value={mode} onChange={(_, value) => changeMode(value)} variant="scrollable" scrollButtons="auto" aria-label="Quick Book catalog mode">
          {CATALOG_MODES.map((value) => <Tab key={value} value={value} label={quickBookModeLabel(value)} disabled={busy} />)}
        </Tabs>
      </Card>

      {message && <Alert severity={message.severity} action={message.reload ? <Button color="inherit" size="small" onClick={loadCatalog}>Reload</Button> : undefined} sx={{ mb: 1 }}>{message.text}</Alert>}
      {loadError && <Alert severity="error" action={<Button color="inherit" size="small" onClick={loadCatalog}>Retry</Button>} sx={{ mb: 1 }}>{loadError}</Alert>}
      {offeringsError && <Alert severity="error" sx={{ mb: 1 }}>Offering options unavailable: {offeringsError}</Alert>}
      {treeErrors.length > 0 && <Alert severity="error" sx={{ mb: 1 }}><Typography sx={{ fontSize: ".8rem", fontWeight: 700 }}>Fix the catalog before saving or publishing:</Typography>{treeErrors.slice(0, 8).map((error) => <Typography key={error} component="div" sx={{ fontSize: ".75rem" }}>• {error}</Typography>)}</Alert>}

      {loading ? (
        <Box sx={{ display: "grid", minHeight: 280, placeItems: "center" }}><CircularProgress /></Box>
      ) : (
        <Grid container spacing={2} alignItems="flex-start">
          <Grid item xs={12} lg={8}>
            <Card sx={{ p: { xs: 1.25, md: 2 } }}>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ sm: "center" }} sx={{ mb: 1.5 }}>
                <Box sx={{ flex: 1 }}>
                  <Typography component="h2" sx={{ fontWeight: 700 }}>{quickBookModeLabel(mode)} draft</Typography>
                  <Typography color="text.secondary" sx={{ fontSize: ".75rem" }}>Build one to five levels. Each node needs a canonical link; customer copy and image overrides stay independent.</Typography>
                </Box>
                <Chip size="small" label={`Draft v${draft?.version ?? 0}`} />
                <Chip size="small" color={published ? "success" : "default"} label={published ? `Published v${published.version}` : "Not published"} />
              </Stack>
              <Divider sx={{ mb: 1.5 }} />
              {!roots.length && <Alert severity="info" sx={{ mb: 1.5 }}>This mode has an empty draft. Add a root node to start its customer navigation.</Alert>}
              <Box component="form" onSubmit={handleSubmit(saveDraft)} noValidate>
                <CatalogNodes control={control} setValue={setValue} name="roots" depth={1} parentSource={null} offerings={offerings} disabled={busy || !permission.canEdit || Boolean(loadError)} />
                <Stack direction={{ xs: "column", sm: "row" }} spacing={1} justifyContent="flex-end" sx={{ mt: 2 }}>
                  <Button type="submit" variant="outlined" startIcon={saving ? <CircularProgress size={16} /> : <SaveOutlinedIcon />} disabled={busy || !permission.canEdit || Boolean(loadError) || Boolean(offeringsError)}>Save draft</Button>
                  <Button type="button" variant="contained" startIcon={publishing ? <CircularProgress size={16} color="inherit" /> : <PublishIcon />} disabled={busy || !permission.canEdit || !draft || Boolean(loadError) || Boolean(offeringsError)} onClick={requestPublish}>Publish</Button>
                </Stack>
              </Box>
            </Card>
          </Grid>

          <Grid item xs={12} lg={4}>
            <Card sx={{ p: 2, position: { lg: "sticky" }, top: { lg: 80 } }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography component="h2" sx={{ fontWeight: 700 }}>Phone preview</Typography>
                <Chip size="small" variant="outlined" label={isDirty ? "Unsaved" : "Saved"} color={isDirty ? "warning" : "default"} />
              </Stack>
              <Typography color="text.secondary" sx={{ fontSize: ".7rem", mb: 1.5 }}>Preview uses the override image first, then the canonical image.</Typography>
              <Box sx={{ width: "100%", maxWidth: 340, mx: "auto", border: "8px solid", borderColor: "grey.900", borderRadius: 4, bgcolor: "grey.50", p: 1.25, minHeight: 360 }}>
                <Typography sx={{ fontWeight: 800, mb: 1 }}>{quickBookModeLabel(mode)}</Typography>
                {roots.length ? <PreviewNodes nodes={roots} offerings={offerings} mode={mode} /> : <Typography color="text.secondary" sx={{ textAlign: "center", mt: 10 }}>No catalog items yet</Typography>}
              </Box>
              <Divider sx={{ my: 1.5 }} />
              <Typography color="text.secondary" sx={{ fontSize: ".7rem" }}>{publishedAt ? `Last published ${publishedAt}` : "This mode has not been published."}</Typography>
            </Card>
          </Grid>
        </Grid>
      )}

      <Dialog open={publishDialogOpen} onClose={publishing ? undefined : () => setPublishDialogOpen(false)} fullWidth maxWidth="sm" aria-labelledby="publish-catalog-title">
        <DialogTitle id="publish-catalog-title">Publish {quickBookModeLabel(mode)} catalog?</DialogTitle>
        <DialogContent dividers>
          <Alert severity="warning">
            This atomically replaces the customer-visible {quickBookModeLabel(mode)} catalog with draft v{draft?.version ?? 0}. Existing booking requests keep their saved service terms.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPublishDialogOpen(false)} disabled={publishing}>Cancel</Button>
          <Button variant="contained" color="warning" onClick={publish} disabled={publishing} startIcon={publishing ? <CircularProgress size={16} /> : <PublishIcon />}>Confirm publish</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
